import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as signalR from "@microsoft/signalr";
import { useSelector } from "react-redux";
import { uiActions } from "../store/ui/ui-slice";
import useRoutePermission from "../Hooks/useRoutePermission";
import { logoutUser } from "../store/user/user-action";
import { dispatch } from "../store";

const MarketWatchContext = createContext();
export const useChat = () => useContext(MarketWatchContext);

export const MarketWatchProvider = ({ children }) => {
  const connectionRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const { sToken, sUrl, user } = useSelector((state) => state.user);
  const { isMarketWatch } = useRoutePermission({ user });

  useEffect(() => {
    if (!sToken || !sUrl || !isMarketWatch) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(sUrl, {
        accessTokenFactory: () => sToken,
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      // .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Lifecycle listeners
    connection.onreconnecting(() => {
      console.warn("SignalR reconnecting...");
      setConnected(false);
    });

    connection.onreconnected(() => {
      console.log("SignalR reconnected.");
      setConnected(true);
    });

    connection.onclose(() => {
      console.warn(" SignalR connection closed.");
      setConnected(false);
    });

    const startConnection = async () => {
      if (
        connection.state === signalR.HubConnectionState.Disconnected &&
        navigator.onLine
      ) {
        try {
          await connection.start();
          console.log("SignalR connected.");
          setConnected(true);
        } catch (err) {
          console.error("SignalR failed to connect:", err);
          setConnected(false);
          logoutUser(dispatch);
        }
      }
    };

    // Start initial connection
    connectionRef.current = connection;
    startConnection();

    // --- Reconnect Triggers ---
    const tryReconnect = () => {
      if (!connection) return;

      const isSignalRConnected =
        connection.state === signalR.HubConnectionState.Connected;

      if (isSignalRConnected && !connected) {
        // Already connected, just update state
        console.log("SignalR already connected, updating state...");
        setConnected(true);
      }
    };

    const handleOffline = () => {
      console.warn("Internet disconnected");
      dispatch(uiActions.setInternetConnection(false));
      setConnected(false);
    };

    const handleOnline = () => {
      dispatch(uiActions.setInternetConnection(true));
      console.log(" Internet reconnected");
      tryReconnect();
    };

    // Listeners
    document.addEventListener("visibilitychange", tryReconnect);
    window.addEventListener("focus", tryReconnect);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    // Fallback polling (10s interval)
    // const intervalId = setInterval(() => {
    //   if (
    //     navigator.onLine &&
    //     connection.state === signalR.HubConnectionState.Disconnected
    //   ) {
    //     console.log("⏱️ Poll: Reconnecting...");
    //     startConnection();
    //   }
    // }, 10000);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", tryReconnect);
      window.removeEventListener("focus", tryReconnect);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);

      connection
        .stop()
        .then(() => console.log(" SignalR disconnected"))
        .catch((err) => console.error("SignalR stop error:", err));
    };
  }, [sToken, sUrl, user, isMarketWatch]);

  const on = (event, callback) => {
    connectionRef.current?.on(event, callback);
  };

  const off = (event) => {
    connectionRef.current?.off(event);
  };

  const send = async (method, ...args) => {
    try {
      await connectionRef.current?.invoke(method, ...args);
    } catch (err) {
      console.error(`SignalR send error (${method}):`, err);
    }
  };

  return (
    <MarketWatchContext.Provider value={{ connected, on, off, send }}>
      {children}
    </MarketWatchContext.Provider>
  );
};
